/* eslint-disable no-param-reassign -- immer draft mutations in Zustand */

import type {
  Issue,
  Config as LinterConfig,
  LinterResult
} from '@digibee/linter';
import { wrap } from 'comlink';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { generateFlowSpecFromStores } from './helpers';
import {
  applyInvalidAliasIssuesToResult,
  overlayDraftDoubleBracesAlias,
  type DraftDoubleBracesAlias
} from './helpers/mergeInvalidDoubleBracesAliasIssues';

import {
  useComponentConfigStore,
  useConnectorStore,
  useDesignUIStore,
  useTrackStore,
  useTriggerStore
} from '.';

import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import { getFeatureFlag } from '~/common/helpers/globalFeatureFlags';
import type { Nullish } from '~/common/types/Nullish';
import type { LinterWorker } from '~/scenes/Build/scenes/FlowBuilder/components/Design/worker';

export type LinterConfigWithDisabled = LinterConfig & { disabled: boolean };

export type LinterResultWithIssues = LinterResult & {
  issues: [string, Issue[]][];
  count: number;
};

type LinterState = {
  linterConfig: LinterConfigWithDisabled;
  linterResult: LinterResultWithIssues;
  showLinterOnCanvas: boolean;
  showingLinterTooltip: string | undefined;
  draftDoubleBracesAlias: DraftDoubleBracesAlias;
};

type LinterActions = {
  setLinterConfig: (config: LinterConfigWithDisabled) => void;
  setLinterResult: (result: LinterResultWithIssues) => void;
  toggleLinterOnCanvas: () => void;
  enableLinter: () => void;
  disableLinter: () => void;
  setShowingLinterTooltip: (elementId: string | undefined) => void;
  clearShowingLinterTooltip: () => void;
  setDraftDoubleBracesAlias: (componentId: string, alias: string) => void;
  clearDraftDoubleBracesAlias: () => void;
  setIssues: (params: {
    linterResult: LinterResult;
    tracksNames: string[];
    isCapsule: boolean;
  }) => void;
  reset: () => void;
};

type LinterStore = LinterState & LinterActions;

const initialLinterResult: LinterResultWithIssues = {
  hasError: false,
  isInvalid: false,
  trigger: [],
  tracks: {},
  components: {},
  issues: [],
  count: 0
};

const initialState: LinterState = {
  linterConfig: {
    disabled: false,
    rules: {}
  },
  linterResult: initialLinterResult,
  showLinterOnCanvas: true,
  showingLinterTooltip: undefined,
  draftDoubleBracesAlias: null
};

const collectComponentIssues = (
  linterResult: LinterResult,
  tracksNames: string[]
) =>
  tracksNames.flatMap(trackName => {
    const trackComponentsIssues = linterResult.components?.[trackName] || {};

    return Object.entries(trackComponentsIssues).filter(
      ([, currentIssues]) => currentIssues.length > 0
    );
  });

export const useLinterStore = create<LinterStore>()(
  devtools(
    immer(set => ({
      ...initialState,

      setLinterConfig: config => set({ linterConfig: config }),

      setLinterResult: result => set({ linterResult: result }),

      toggleLinterOnCanvas: () =>
        set(state => {
          state.showLinterOnCanvas = !state.showLinterOnCanvas;
        }),

      enableLinter: () =>
        set(state => {
          state.showLinterOnCanvas = true;
        }),

      disableLinter: () =>
        set(state => {
          state.showLinterOnCanvas = false;
        }),

      setShowingLinterTooltip: elementId =>
        set(state => {
          state.showingLinterTooltip = elementId;
        }),

      clearShowingLinterTooltip: () =>
        set(state => {
          state.showingLinterTooltip = undefined;
        }),

      setDraftDoubleBracesAlias: (componentId, alias) =>
        set(state => {
          state.draftDoubleBracesAlias = { componentId, alias };
        }),

      clearDraftDoubleBracesAlias: () =>
        set(state => {
          state.draftDoubleBracesAlias = null;
        }),

      setIssues: ({ linterResult, tracksNames, isCapsule }) =>
        set(state => {
          const componentsIssues = collectComponentIssues(
            linterResult,
            tracksNames
          );

          const triggerIssues =
            linterResult.trigger.length > 0 && !isCapsule
              ? ['trigger', linterResult.trigger]
              : [];

          const issues = [triggerIssues, ...componentsIssues].filter(
            item => item.length > 0
          ) as [string, Issue[]][];

          const count = issues.reduce(
            (acc, [, componentIssues]) => acc + (componentIssues?.length || 0),
            0
          );

          const trigger = isCapsule ? [] : linterResult.trigger;

          state.linterResult = {
            ...linterResult,
            trigger,
            issues,
            count
          };
        }),

      reset: () => set({ ...initialState })
    })),
    { name: 'LinterStore' }
  )
);

export const setLinterConfig = (config: LinterConfigWithDisabled) =>
  useLinterStore.getState().setLinterConfig(config);

export const setLinterResult = (result: LinterResultWithIssues) =>
  useLinterStore.getState().setLinterResult(result);

export const toggleLinterOnCanvas = () =>
  useLinterStore.getState().toggleLinterOnCanvas();

export const enableLinter = () => useLinterStore.getState().enableLinter();

export const disableLinter = () => useLinterStore.getState().disableLinter();

export const setShowingLinterTooltip = (elementId: string | undefined) =>
  useLinterStore.getState().setShowingLinterTooltip(elementId);

export const clearShowingLinterTooltip = () =>
  useLinterStore.getState().clearShowingLinterTooltip();

export const setIssues = (params: {
  linterResult: LinterResult;
  tracksNames: string[];
  isCapsule: boolean;
}) => useLinterStore.getState().setIssues(params);

export const resetLinterStore = () => useLinterStore.getState().reset();

export const isLinterEnabled = () =>
  !useLinterStore.getState().linterConfig.disabled;

export const getLinterIssuesCount = () =>
  useLinterStore.getState().linterResult.count;

export const getLinterIssues = () =>
  useLinterStore.getState().linterResult.issues;

export const useLinterIssues = () => {
  const linterResult = useLinterStore(state => state.linterResult);
  const showLinterOnCanvas = useLinterStore(state => state.showLinterOnCanvas);

  return {
    issues: linterResult?.issues,
    hasError: linterResult?.hasError,
    count: linterResult?.count,
    showLinterOnCanvas
  };
};

let lastLinterWorker: Nullish<Worker> = null;

const getConfigurationsWithDraftAlias = () => {
  const { configurations } = useComponentConfigStore.getState();
  const { draftDoubleBracesAlias } = useLinterStore.getState();

  return overlayDraftDoubleBracesAlias(configurations, draftDoubleBracesAlias);
};

export const applyInvalidDoubleBracesAliasWarnings = () => {
  const { tracks, getTrackByComponent } = useTrackStore.getState();
  const current = useLinterStore.getState().linterResult;

  const linterResult = applyInvalidAliasIssuesToResult(
    {
      hasError: current.hasError,
      isInvalid: current.isInvalid,
      trigger: current.trigger ?? [],
      tracks: current.tracks ?? {},
      components: current.components ?? {}
    },
    getConfigurationsWithDraftAlias(),
    id => getTrackByComponent(id)?.name
  );

  setIssues({
    linterResult,
    tracksNames: Array.from(tracks.values()).map(track => track.name),
    isCapsule: useDesignUIStore.getState().isCapsule
  });
};

export const setDraftDoubleBracesAlias = (
  componentId: string,
  alias: string
) => {
  useLinterStore.getState().setDraftDoubleBracesAlias(componentId, alias);
  applyInvalidDoubleBracesAliasWarnings();
};

export const clearDraftDoubleBracesAlias = () => {
  useLinterStore.getState().clearDraftDoubleBracesAlias();
  applyInvalidDoubleBracesAliasWarnings();
};

export const runLinter = async () => {
  const { connectors } = useConnectorStore.getState();
  const triggerSpec = useTriggerStore.getState().trigger;

  const isDoubleBracesAliasLinterEnabled = getFeatureFlag(
    featureFlagConstants.BUILD_ENABLE_DOUBLE_BRACES_ALIAS_LINTER
  );

  if (lastLinterWorker) lastLinterWorker.terminate();

  const flowSpec = generateFlowSpecFromStores();

  const worker = new Worker(
    new URL('../components/Design/worker', import.meta.url),
    {
      name: 'linter-worker',
      type: 'module'
    }
  );

  lastLinterWorker = worker;

  const workerApi = wrap<LinterWorker>(worker);

  const linterResultPromise = workerApi.linter({
    flowSpec,
    triggerSpec,
    connectors,
    config: {
      rules: {
        'double-braces-aliases-validator': {
          disabled: !isDoubleBracesAliasLinterEnabled
        }
      }
    }
  });

  let workerResult: LinterResult;
  try {
    workerResult = await linterResultPromise;
  } catch {
    return;
  }

  if (worker !== lastLinterWorker) return;

  const { tracks, getTrackByComponent } = useTrackStore.getState();
  const linterResult = applyInvalidAliasIssuesToResult(
    workerResult,
    getConfigurationsWithDraftAlias(),
    id => getTrackByComponent(id)?.name
  );
  setIssues({
    linterResult,
    tracksNames: Array.from(tracks.values()).map(track => track.name),
    isCapsule: useDesignUIStore.getState().isCapsule
  });
};
